import React from 'react';

type Direction = 'row' | 'col' | 'row-reverse' | 'col-reverse';
type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
type Display = 'flex' | 'grid' | 'inline-flex' | 'inline-grid' | 'block' | 'inline';
type Wrap = 'wrap' | 'nowrap' | 'wrap-reverse';

interface BoxOwnProps {
  as?: React.ElementType;
  display?: Display;
  direction?: Direction;
  align?: Align;
  justify?: Justify;
  gap?: number | string;
  wrap?: Wrap;
  className?: string;
  children?: React.ReactNode;
}

type BoxProps<C extends React.ElementType = 'div'> = BoxOwnProps &
  Omit<React.ComponentPropsWithRef<C>, keyof BoxOwnProps>;

const DIRECTION_MAP: Record<Direction, string> = {
  row: 'flex-row',
  col: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'col-reverse': 'flex-col-reverse',
};

const ALIGN_MAP: Record<Align, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const JUSTIFY_MAP: Record<Justify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

const DISPLAY_MAP: Record<Display, string> = {
  flex: 'flex',
  grid: 'grid',
  'inline-flex': 'inline-flex',
  'inline-grid': 'inline-grid',
  block: 'block',
  inline: 'inline',
};

const WRAP_MAP: Record<Wrap, string> = {
  wrap: 'flex-wrap',
  nowrap: 'flex-nowrap',
  'wrap-reverse': 'flex-wrap-reverse',
};

const GAP_MAP: Record<string, string> = {
  '0': 'gap-0', '0.5': 'gap-0.5', '1': 'gap-1', '1.5': 'gap-1.5',
  '2': 'gap-2', '2.5': 'gap-2.5', '3': 'gap-3', '4': 'gap-4',
  '5': 'gap-5', '6': 'gap-6', '8': 'gap-8', '10': 'gap-10', '12': 'gap-12',
};

function Box<C extends React.ElementType = 'div'>(
  {
    as,
    display = 'flex',
    direction,
    align,
    justify,
    gap,
    wrap,
    className = '',
    children,
    ...rest
  }: BoxProps<C>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const Component = as || 'div';

  const classes = [
    DISPLAY_MAP[display],
    direction && DIRECTION_MAP[direction],
    align && ALIGN_MAP[align],
    justify && JUSTIFY_MAP[justify],
    wrap && WRAP_MAP[wrap],
    gap !== undefined && (GAP_MAP[String(gap)] || `gap-[${gap}]`),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component ref={ref} className={classes} {...rest}>
      {children}
    </Component>
  );
}

const ForwardedBox = React.forwardRef(Box) as <C extends React.ElementType = 'div'>(
  props: BoxProps<C> & { ref?: React.ForwardedRef<HTMLElement> },
) => React.ReactElement | null;

export default ForwardedBox;
export type { BoxProps };
